import { objectSpread2 as _objectSpread2, slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, extends as _extends, defineProperty as _defineProperty } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TableSelectRow, TableSelectAll, DataTable, TableContainer, Table, TableHead, TableRow, TableExpandHeader, TableBody, TableExpandRow, TableExpandedRow, TableHeader, TableToolbar, TableToolbarContent, TableCell, DataTableSkeleton, TableBatchActions, TableBatchAction } from 'carbon-components-react';
import { IlSearchV2 as TranslatedIlSearchV2 } from '../IlSearchV2/index.js';
import { IlPaginationV2 } from '../IlPaginationV2/index.js';
import { IlButton } from '../IlButton/index.js';
import { IlOverflowMenu } from '../IlOverflowMenu/IlOverflowMenu/index.js';
import { IlOverflowMenuItem } from '../IlOverflowMenu/IlOverflowMenuItem/index.js';
import { IlIcon } from '../IlIcon/index.js';
import { FW_PREFIX, cssClassPrefix } from '../../shared/constants/generalConstants.js';
import { polyfillClosest } from '../../shared/utils/DOMpolyfills.js';

polyfillClosest();
var FW_CSS = "".concat(FW_PREFIX, "--data-table-group");
var BASE_CSS = "".concat(cssClassPrefix, "--data-table-group");
var controlSelector = 'button, a, select, input, textarea, [role=button], [role=link]';
var IlDataTableV2 = function IlDataTableV2(props) {
  var tableRef = useRef();
  var _props$description = props.description,
    description = _props$description === void 0 ? '' : _props$description,
    _props$title = props.title,
    title = _props$title === void 0 ? '' : _props$title,
    _props$id = props.id,
    tableId = _props$id === void 0 ? 'tableId' : _props$id,
    stickyHeader = props.stickyHeader,
    _props$pagination = props.pagination,
    pagination = _props$pagination === void 0 ? {} : _props$pagination,
    toolbar = props.toolbar,
    _props$batchActions = props.batchActions,
    batchActions = _props$batchActions === void 0 ? {} : _props$batchActions,
    _props$expansion = props.expansion,
    expansion = _props$expansion === void 0 ? {} : _props$expansion,
    _props$isLoading = props.isLoading,
    isLoading = _props$isLoading === void 0 ? false : _props$isLoading,
    tableClassName = props.className,
    headers = props.headers,
    dataSource = props.dataSource,
    _props$canAutoFocus = props.canAutoFocus,
    canAutoFocus = _props$canAutoFocus === void 0 ? true : _props$canAutoFocus,
    _props$sortingDirecti = props.sortingDirections,
    sortingDirections = _props$sortingDirecti === void 0 ? ['ASC', 'DESC', 'NONE'] : _props$sortingDirecti,
    _props$defaultSortHea = props.defaultSortHeader,
    defaultSortHeader = _props$defaultSortHea === void 0 ? '' : _props$defaultSortHea,
    _props$defaultSortDir = props.defaultSortDirection,
    defaultSortDirection = _props$defaultSortDir === void 0 ? 0 : _props$defaultSortDir;
  var _batchActions$hasSele = batchActions.hasSelectAll,
    hasSelectAll = _batchActions$hasSele === void 0 ? false : _batchActions$hasSele,
    _batchActions$hasSele2 = batchActions.hasSelectRow,
    hasSelectRow = _batchActions$hasSele2 === void 0 ? false : _batchActions$hasSele2,
    _batchActions$actions = batchActions.actions,
    batchActionsButtons = _batchActions$actions === void 0 ? [] : _batchActions$actions;
  var _expansion$isActive = expansion.isActive,
    isExpansionActive = _expansion$isActive === void 0 ? false : _expansion$isActive,
    _expansion$hasExpandA = expansion.hasExpandAll,
    hasExpandAll = _expansion$hasExpandA === void 0 ? false : _expansion$hasExpandA,
    expansionClassName = expansion.className,
    _expansion$renderExpa = expansion.renderExpandedRow,
    renderExpandedRow = _expansion$renderExpa === void 0 ? function () {
      return false;
    } : _expansion$renderExpa;
  var _useState = useState({
      row: 0,
      col: 0
    }),
    _useState2 = _slicedToArray(_useState, 2),
    selectedCell = _useState2[0],
    setSelectedCell = _useState2[1];

  // Sort's directions for custom sorting callback
  var sortDirections = sortingDirections;
  var _useState3 = useState(defaultSortHeader),
    _useState4 = _slicedToArray(_useState3, 2),
    sortHeader = _useState4[0],
    setSortHeader = _useState4[1];
  var _useState5 = useState(defaultSortDirection),
    _useState6 = _slicedToArray(_useState5, 2),
    sortDirectionIndex = _useState6[0],
    setSortDirectionIndex = _useState6[1];

  /**
   * sets tab indices of all desendants of a given container
   * useful for cell content determined externally
   * and elements burried in Carbon dependency logic
   * @param {Element} container
   * @param {string} selector
   * @param {number} value the tabindex attribute/property value to set
   * @param {boolean} focusFirst whether to focus the first descendant node that matches
   * @param {boolean} excludeActive whether to exclude nodes with explicit tabindex attribute values of zero
   */
  var setDescendantTabIndices = function setDescendantTabIndices(container, selector, value, focusFirst, excludeActive) {
    var nodes = _toConsumableArray(container.querySelectorAll(selector));
    if (excludeActive) {
      nodes = nodes.filter(function (node) {
        return node.dataset.active !== 'true';
      });
    }
    nodes.forEach(function (nodeParam, index) {
      var node = nodeParam;
      node.tabIndex = value;
      node.dataset.active = !value;
      if (focusFirst && index === 0) {
        node.focus();
      }
    });
  };

  /**
   * Accessibility Method. Moves focus to any cell in the table
   *
   * @param newRow Row number of cell, stored in data-aria-rowindex
   * @param newCol Col number of cell, stored in data-aria-colindex
   */
  var moveCellFocusTo = function moveCellFocusTo(newRow, newCol, e, drillDownToControl) {
    var newFocusedRow = tableRef.current.querySelector("[aria-rowindex=\"".concat(newRow + 1, "\"]"));
    var newFocusedCell = newFocusedRow && newFocusedRow.cells[newCol];
    var table = tableRef.current.querySelector('table');
    // if we arrived here via a click event
    // let the browser handle focus rather than focusing the first control in the cell
    // since we may be clicking the second or third control in the same cell
    var focusFirst = e && e.type !== 'click';
    var expandableContentSelector = newFocusedCell && "#".concat(newFocusedCell.dataset.ariaOwns);
    var expandableContent = expandableContentSelector && table.querySelector(expandableContentSelector);

    // remove everything previously set to be tabbable within our table from the tab sequence
    // including controls within cell content set externally
    // and custom Carbon subcomponent cells
    setDescendantTabIndices(table, controlSelector, -1);
    setDescendantTabIndices(table, 'th, td', -1);
    if (newFocusedCell) {
      // first set our selectedCell to {-1,-1} to be sure state will register a change
      // ie: when returning focus from an internal control to a cell
      // the coordinates are not changing and would not trigger any rerender
      setSelectedCell({
        row: -1,
        col: -1
      });
      setSelectedCell({
        row: newRow,
        col: newCol
      });
      if (drillDownToControl) {
        // if we are drilling down to controls via enter key press or clicking on them
        // explicitly make our parent cell untabbable
        // even though it corresponds to our active cell coordinates
        newFocusedCell.tabIndex = -1;
        // make all descendant controls of the relevant cell tabbable
        // focus the first control in the cell if and only if we are pressing the enter key to drill down
        setDescendantTabIndices(newFocusedCell, controlSelector, 0, focusFirst);
        if (expandableContent) {
          setDescendantTabIndices(expandableContent, controlSelector, 0);
        }
      } else {
        // wait for all subcomponents  to render
        // including cell content set externally
        setTimeout(function () {
          newFocusedCell.tabIndex = 0; // failsafe for checkbox and expand/collapse cells
          newFocusedCell.focus();
        });
      }
      if (e && e.key !== 'Enter') {
        // prevent default scroll actions when keyboard navigation is handled above
        e.preventDefault();
      }
    }
  };

  /**
   * Accessibility Callback. Moves selected cell and focus based on keyboard key
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Grid_Role#keyboard_interactions
   */
  var keyboardHandler = useCallback(function (e) {
    // do not handle navigation when focus is on form controls within cells
    // except the escape key for focusing on the  cell containing the control
    if (e.target.matches(controlSelector) && e.key !== 'Escape') {
      return;
    }
    switch (e.key) {
      case 'ArrowRight':
      case 'Right':
        // IE11
        moveCellFocusTo(selectedCell.row, selectedCell.col + 1, e);
        break;
      case 'ArrowLeft':
      case 'Left':
        // IE11
        moveCellFocusTo(selectedCell.row, selectedCell.col - 1, e);
        break;
      case 'ArrowDown':
      case 'Down':
        // IE11
        moveCellFocusTo(selectedCell.row + 1, selectedCell.col, e);
        break;
      case 'ArrowUp':
      case 'Up':
        // IE11
        moveCellFocusTo(selectedCell.row - 1, selectedCell.col, e);
        break;
      case 'PageDown':
        if (selectedCell.row + 10 < dataSource.length) {
          moveCellFocusTo(selectedCell.row + 10, selectedCell.col, e);
        } else {
          moveCellFocusTo(dataSource.length, selectedCell.col, e);
        }
        break;
      case 'PageUp':
        if (selectedCell.row - 10 > 1) {
          moveCellFocusTo(selectedCell.row - 10, selectedCell.col, e);
        } else {
          moveCellFocusTo(1, selectedCell.col);
        }
        break;
      case 'Home':
        moveCellFocusTo(selectedCell.row, 0, e);
        break;
      case 'End':
        moveCellFocusTo(selectedCell.row, e.target.parentElement.cells.length - 1, e);
        break;
      case 'Enter':
        moveCellFocusTo(selectedCell.row, selectedCell.col, e, true);
        break;
      case 'Escape':
        moveCellFocusTo(selectedCell.row, selectedCell.col, e);
        break;
    }
  });

  /**
   * Accessibility Hook. Allows keyboard navigation through cells
   */
  useEffect(function () {
    var table = tableRef.current.querySelector('table');
    var checkboxCellSelector = '.bx--table-column-checkbox';
    if (table) {
      setDescendantTabIndices(table, checkboxCellSelector, -1);
      setDescendantTabIndices(table, controlSelector, -1, false, true);
      table.addEventListener('keydown', keyboardHandler, false);
    }
    return function () {
      if (table) {
        table.removeEventListener('keydown', keyboardHandler, false);
      }
    };
  }, [keyboardHandler, tableRef]);
  useEffect(function () {
    if (document.activeElement === document.body) {
      if (canAutoFocus) {
        // If the UI has lost the focus, focus our table
        moveCellFocusTo(0, 0);
      }
    } else {
      // otherwise make sure our first cell, including expand arrow and checkbox cells, is tabindexed
      setSelectedCell({
        row: 0,
        col: 0
      });
    }
  }, []);

  /**
   * add missing screen reader affordances for expandable rows
   */
  useEffect(function () {
    var expandButtons = _toConsumableArray(tableRef.current.querySelectorAll('.bx--table-expand__button'));
    expandButtons.forEach(function (button) {
      var expandableRowContentId = button.closest('tr').dataset.ariaOwns;
      var cell = button.closest('td');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', expandableRowContentId);
      if (cell) {
        cell.dataset.ariaOwns = expandableRowContentId;
      }
    });
  }, [tableRef]);

  /**
   * add screen reader announcements for multiple row selection and pagination status
   * through live regions, we can insure that the above will be announced whenever they change
   * regardless of where focus is
   */
  useEffect(function () {
    var rowCountContainer = tableRef.current.querySelector('.bx--pagination__items-count');
    var selectionCountContainer = tableRef.current.querySelector('.bx--batch-summary__para');
    if (rowCountContainer) {
      rowCountContainer.setAttribute('aria-live', 'polite');
      rowCountContainer.setAttribute('aria-atomic', 'true');
    }
    if (selectionCountContainer) {
      selectionCountContainer.setAttribute('aria-live', 'polite');
      selectionCountContainer.setAttribute('aria-atomic', 'true');
    }
  }, [tableRef]);

  /**
   * Renders Table Headers, also known as table columns
   */
  var renderTableHeaders = function renderTableHeaders(header, getHeaderProps, index) {
    var key = header.key,
      headerName = header.header,
      className = header.className,
      isSortable = header.isSortable,
      alignment = header.alignment,
      onSort = header.onSort;
    var _getHeaderProps = getHeaderProps({
        header: header
      }),
      sortDirection = _getHeaderProps.sortDirection,
      isSortHeader = _getHeaderProps.isSortHeader,
      onClick = _getHeaderProps.onClick;
    var onSortHandler;

    // Has Custom Sort
    if (onSort) {
      onSortHandler = function onSortHandler() {
        var newIndex = sortDirectionIndex + 1;
        if (newIndex >= sortDirections.length || sortHeader !== headerName) {
          // Reset index
          newIndex = 0;
        }
        setSortDirectionIndex(newIndex);
        setSortHeader(headerName);
        // Returns the direction to sort by
        onSort({
          sortDirection: sortDirections[newIndex],
          header: _objectSpread2({}, header)
        });
      };
    }
    return /*#__PURE__*/React__default.createElement(TableHeader, _extends({
      key: key
    }, getHeaderProps({
      header: header
    }), {
      className: classNames(className, _defineProperty({}, "".concat(BASE_CSS, "--header-align-").concat(alignment), alignment), _defineProperty({}, "".concat(BASE_CSS, "--header-sortable"), isSortable), _defineProperty({}, "".concat(BASE_CSS, "--header-sorted-none"), isSortable && !isSortHeader && !onSort), // Default sort
      _defineProperty({}, "".concat(BASE_CSS, "--header-sorted-none"), onSort && sortHeader !== headerName), // Custom sort
      // Default sort
      _defineProperty({}, "".concat(BASE_CSS, "--header-sorted-").concat(sortDirection.toLowerCase()), isSortHeader), // Custom sort
      _defineProperty({}, "".concat(BASE_CSS, "--header-sorted-").concat(sortDirections[sortDirectionIndex].toLowerCase()), onSort && headerName === sortHeader)),
      isSortable: isSortable,
      onClick: onSort && onSortHandler || onClick // Preserves Carbon's sorting method within onClick
      ,
      tabIndex: "-1",
      "aria-label": headerName
    }), headerName, isSortable && /*#__PURE__*/React__default.createElement("div", {
      className: "".concat(BASE_CSS, "--sort-icon-wrapper")
    }, /*#__PURE__*/React__default.createElement(IlIcon, {
      name: "arrow-down-solid",
      className: "".concat(BASE_CSS, "--sort-icon")
    }), /*#__PURE__*/React__default.createElement(IlIcon, {
      name: "arrow-up-down-solid",
      className: "".concat(BASE_CSS, "--sort-icon-up-down")
    })));
  };

  /**
   * Renders Pagination
   */
  var renderPagination = function renderPagination() {
    var totalItems = pagination.totalItems,
      pageSizes = pagination.pageSizes,
      onChange = pagination.onChange;
    if (totalItems && pageSizes && onChange) {
      return /*#__PURE__*/React__default.createElement(IlPaginationV2, pagination);
    }
    return null;
  };

  /**
   * Renders Table Title
   */
  var renderTitle = function renderTitle() {
    return /*#__PURE__*/React__default.createElement("span", {
      id: tableId
    }, title);
  };

  /**
   * Renders Batch Actions if batchActions.actions (alias batchActionsToolbar) is set
   *
   * @param {*} getBatchActionProps From Carbon's DataTable
   * @param {*} selectedRows From Carbon's DataTable
   */
  var renderBatchActions = function renderBatchActions(getBatchActionProps, selectedRows, selectAll) {
    var _batchActions$visible = batchActions.visibleActions,
      visibleActions = _batchActions$visible === void 0 ? 5 : _batchActions$visible;
    var _getBatchActionProps = getBatchActionProps(),
      shouldShowBatchActions = _getBatchActionProps.shouldShowBatchActions,
      onCancel = _getBatchActionProps.onCancel;

    // Accessibility - Moves focus to first checkbox
    var onCancelAccessibility = function onCancelAccessibility() {
      var checkboxes = tableRef.current.querySelectorAll('*[class$="table-column-checkbox"] > input');
      if (checkboxes[0]) {
        checkboxes[0].focus();
      }
      onCancel();
    };
    return /*#__PURE__*/React__default.createElement(TableBatchActions, _extends({}, getBatchActionProps(), {
      onCancel: onCancelAccessibility
    }), batchActionsButtons.slice(0, visibleActions).map(function (_ref) {
      var buttonTitle = _ref.title,
        _onClick = _ref.onClick;
      return /*#__PURE__*/React__default.createElement(TableBatchAction, {
        key: buttonTitle,
        renderIcon: function renderIcon() {
          return null;
        },
        tabIndex: shouldShowBatchActions ? 0 : -1,
        onClick: function onClick() {
          return _onClick(selectedRows, selectAll);
        }
      }, buttonTitle);
    }), batchActionsButtons.length > visibleActions && /*#__PURE__*/React__default.createElement(IlOverflowMenu, {
      flipped: true,
      tabIndex: shouldShowBatchActions ? 0 : -1,
      renderIcon: function renderIcon() {
        return /*#__PURE__*/React__default.createElement(IlIcon, {
          name: "ellipsis-solid-v2"
        });
      }
    }, batchActionsButtons.slice(visibleActions, batchActionsButtons.length).map(function (_ref2) {
      var buttonTitle = _ref2.title,
        _onClick2 = _ref2.onClick;
      return /*#__PURE__*/React__default.createElement(IlOverflowMenuItem, {
        key: buttonTitle,
        itemText: buttonTitle,
        onClick: function onClick() {
          return _onClick2(selectedRows);
        }
      });
    })));
  };

  /**
   * Renders Batch Action Buttons when there isn't a standalone Toolbar present
   *
   * @param {*} getToolbarProps  From Carbon's DataTable
   * @param {*} getBatchActionProps  From Carbon's DataTable
   * @param {*} selectedRows From Carbon's DataTable
   */
  var renderBatchActionsWithoutToolbar = function renderBatchActionsWithoutToolbar(getToolbarProps, getBatchActionProps, selectedRows) {
    return /*#__PURE__*/React__default.createElement(TableToolbar, getToolbarProps(), batchActionsButtons && renderBatchActions(getBatchActionProps, selectedRows));
  };

  /**
   * Renders Toolbar Buttons
   *
   * @param {*} getBatchActionProps From Carbon's DataTable
   */
  var renderToolbarButtons = function renderToolbarButtons(getBatchActionProps) {
    var _toolbar$visibleButto = toolbar.visibleButtons,
      visibleButtons = _toolbar$visibleButto === void 0 ? 2 : _toolbar$visibleButto,
      _toolbar$buttons = toolbar.buttons,
      toolbarButtons = _toolbar$buttons === void 0 ? [] : _toolbar$buttons;
    var _getBatchActionProps2 = getBatchActionProps(),
      shouldShowBatchActions = _getBatchActionProps2.shouldShowBatchActions;
    var className = "".concat(FW_PREFIX, "__IlButton bx--btn--il-secondary bx--btn bx--btn--secondary");
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, toolbarButtons.length > visibleButtons && /*#__PURE__*/React__default.createElement(IlOverflowMenu, {
      className: className,
      flipped: true,
      tabIndex: shouldShowBatchActions ? -1 : 0,
      renderIcon: function renderIcon() {
        return /*#__PURE__*/React__default.createElement(IlIcon, {
          name: "ellipsis-solid-v2"
        });
      }
    }, toolbarButtons.slice(visibleButtons, toolbarButtons.length).map(function (button) {
      var buttonTitle = button.title,
        onClick = button.onClick;
      return /*#__PURE__*/React__default.createElement(IlOverflowMenuItem, _extends({
        key: buttonTitle,
        itemText: buttonTitle,
        onClick: onClick
      }, button));
    })), toolbarButtons.slice(0, visibleButtons).map(function (button) {
      var buttonTitle = button.title,
        onClick = button.onClick;
      return /*#__PURE__*/React__default.createElement(IlButton, _extends({
        key: buttonTitle,
        tabIndex: shouldShowBatchActions ? -1 : 0,
        onClick: onClick
      }, button), buttonTitle);
    }));
  };

  /**
   * Renders Select All checkbox if present, otherwise renders an empty <th> to match nr. of headers with rows
   *
   * @param {*} getSelectionProps From Carbon's DataTable
   */
  var renderSelectAll = function renderSelectAll(getSelectionProps) {
    var _batchActions$selectA = batchActions.selectAll,
      selectAll = _batchActions$selectA === void 0 ? {} : _batchActions$selectA;
    var _selectAll$ariaLabel = selectAll.ariaLabel,
      ariaLabel = _selectAll$ariaLabel === void 0 ? 'Select all rows' : _selectAll$ariaLabel;
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, hasSelectAll && /*#__PURE__*/React__default.createElement(TableSelectAll, _extends({}, getSelectionProps(), {
      ariaLabel: ariaLabel
    }, selectAll)), !hasSelectAll && hasSelectRow && /*#__PURE__*/React__default.createElement("th", null, " "));
  };

  /**
   * Renders Select Row checkbox if present, otherwise renders an empty <td> to match nr. of rows with headers
   *
   * @param {*} getSelectionProps From Carbon's DataTable
   */
  var renderSelectRow = function renderSelectRow(getSelectionProps) {
    var _batchActions$selectR = batchActions.selectRow,
      selectRow = _batchActions$selectR === void 0 ? {} : _batchActions$selectR;
    var _selectRow$ariaLabel = selectRow.ariaLabel,
      ariaLabel = _selectRow$ariaLabel === void 0 ? 'Select current row' : _selectRow$ariaLabel;
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, hasSelectRow && /*#__PURE__*/React__default.createElement(TableSelectRow, _extends({}, getSelectionProps(), {
      ariaLabel: ariaLabel
    }, selectRow)), !hasSelectRow && hasSelectAll && /*#__PURE__*/React__default.createElement("td", null, " "));
  };

  /**
   * Renders Toolbar if toolbar prop is present
   *
   * @param {*} getToolbarProps From Carbon's DataTable
   * @param {*} getBatchActionProps From Carbon's DataTable, goes to renderBatchActions
   * @param {*} selectedRows From Carbon's DataTable, goes to renderBatchActions
   */
  var renderToolbar = function renderToolbar(getToolbarProps, getBatchActionProps, selectedRows, selectAll) {
    var _toolbar$search = toolbar.search,
      search = _toolbar$search === void 0 ? {} : _toolbar$search,
      _toolbar$filters = toolbar.filters,
      filters = _toolbar$filters === void 0 ? {} : _toolbar$filters,
      toolbarRenderer = toolbar.render;
    var searchOnChange = search.onChange,
      searchClassName = search.className;
    var renderFilters = filters.renderFilters,
      filterClassName = filters.className;
    return /*#__PURE__*/React__default.createElement(TableToolbar, getToolbarProps(), batchActionsButtons && renderBatchActions(getBatchActionProps, selectedRows), /*#__PURE__*/React__default.createElement(TableToolbarContent, null, searchOnChange && /*#__PURE__*/React__default.createElement(TranslatedIlSearchV2, _extends({}, search, {
      tabIndexValue: getBatchActionProps().shouldShowBatchActions ? -1 : 0,
      className: classNames(searchClassName, "".concat(BASE_CSS, "--toolbar-search")),
      onChange: searchOnChange
    })), renderFilters && /*#__PURE__*/React__default.createElement("div", {
      className: "".concat(BASE_CSS, "--toolbar-filters")
    }, /*#__PURE__*/React__default.createElement("div", {
      tabIndex: getBatchActionProps().shouldShowBatchActions ? -1 : 0,
      className: classNames(filterClassName, "".concat(BASE_CSS, "--toolbar-filters-btn"))
    }, renderFilters())), /*#__PURE__*/React__default.createElement("div", {
      className: "".concat(BASE_CSS, "--toolbar-buttons")
    }, renderToolbarButtons(getBatchActionProps)), toolbarRenderer && toolbarRenderer()));
  };

  /**
   * Renders Row's Content
   */
  var renderRow = function renderRow(row, columns, getSelectionProps, rowIndex) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderSelectRow(function () {
      return getSelectionProps({
        row: row
      });
    }), row.cells.map(function (_ref3, cellIndex) {
      var id = _ref3.id,
        header = _ref3.info.header,
        value = _ref3.value;
      var _columns$find = columns.find(function (c) {
          return c.key === header;
        }),
        renderCell = _columns$find.renderCell,
        alignment = _columns$find.alignment;
      // Uses .find to retain the order even when sorted, don't use dataSource[rowIndex]
      var rowData = dataSource.find(function (d) {
        return d.id === row.id;
      });
      var columnOffset = Number(hasSelectRow) + Number(expansion.isActive);
      return /*#__PURE__*/React__default.createElement(TableCell, {
        key: id,
        "aria-colindex": cellIndex + 1,
        tabIndex: selectedCell.row === rowIndex + 1 && selectedCell.col === cellIndex + columnOffset ? 0 : -1
      }, /*#__PURE__*/React__default.createElement("div", {
        className: classNames(_defineProperty(_defineProperty({}, "".concat(BASE_CSS, "--custom-cell"), renderCell), "".concat(BASE_CSS, "--row-align-").concat(alignment), alignment))
      }, renderCell ? renderCell(rowData) : value));
    }));
  };

  /**
   * Renders Skeleton Table when isLoading is true
   */
  var renderSkeleton = function renderSkeleton() {
    return /*#__PURE__*/React__default.createElement(DataTableSkeleton, {
      headers: headers,
      showHeader: false,
      showToolbar: false,
      zebra: true
    });
  };

  /**
   * adjusts the table's active cell coordinates when a cell is focused via click
   */
  var tableClickHandler = function tableClickHandler(e) {
    var cell = e.target.closest('th, td');
    var row = cell === null || cell === void 0 ? void 0 : cell.parentElement;
    var control = e.target.closest(controlSelector);
    var targetIsControl = cell && control && cell.contains(control);
    if (row) {
      moveCellFocusTo(Number(row.getAttribute('aria-rowindex') - 1), cell.cellIndex, null, targetIsControl);
    }
    if (control && control.dataset.active) {
      // announce the expanded/collapsed state of expand/collapse button in screen reader
      control.setAttribute('aria-expanded', control.dataset.active);

      // wait for the cells data attribute to update
      setTimeout(function () {
        // anounce expandable row content as if it were part of the expand/collapse button cell
        // if and only if it is expanded
        if (cell.dataset.active === 'true') {
          cell.setAttribute('aria-owns', cell.dataset.ariaOwns);
        } else {
          cell.removeAttribute('aria-owns');
        }
      });
    }
  };

  /**
   * Renders Data Table
   */
  var renderDataTable = function renderDataTable() {
    return /*#__PURE__*/React__default.createElement(DataTable, {
      rows: dataSource,
      headers: headers,
      stickyHeader: stickyHeader
    }, function (_ref4) {
      var rows = _ref4.rows,
        columns = _ref4.headers,
        getHeaderProps = _ref4.getHeaderProps,
        getRowProps = _ref4.getRowProps,
        getTableProps = _ref4.getTableProps,
        selectedRows = _ref4.selectedRows,
        getTableContainerProps = _ref4.getTableContainerProps,
        getSelectionProps = _ref4.getSelectionProps,
        getToolbarProps = _ref4.getToolbarProps,
        getBatchActionProps = _ref4.getBatchActionProps,
        getExpandHeaderProps = _ref4.getExpandHeaderProps,
        selectAll = _ref4.selectAll;
      if (props.onRenderProps) {
        props.onRenderProps({
          rows: rows,
          headers: columns,
          getHeaderProps: getHeaderProps,
          getRowProps: getRowProps,
          getTableProps: getTableProps,
          selectedRows: selectedRows,
          getTableContainerProps: getTableContainerProps,
          getSelectionProps: getSelectionProps,
          getToolbarProps: getToolbarProps,
          getBatchActionProps: getBatchActionProps,
          getExpandHeaderProps: getExpandHeaderProps,
          selectAll: selectAll
        });
      }
      return /*#__PURE__*/React__default.createElement(TableContainer, _extends({}, getTableContainerProps(), {
        title: title && renderTitle(),
        description: description
      }), toolbar && renderToolbar(getToolbarProps, getBatchActionProps, selectedRows), !toolbar && (hasSelectAll || hasSelectRow) && renderBatchActionsWithoutToolbar(getToolbarProps, getBatchActionProps, selectedRows), isLoading && renderSkeleton(), !isLoading && /*#__PURE__*/React__default.createElement(Table, _extends({}, getTableProps(), {
        role: "grid",
        "aria-labelledby": tableId,
        isSortable: true,
        useZebraStyles: true,
        onClick: tableClickHandler
      }), /*#__PURE__*/React__default.createElement(TableHead, null, /*#__PURE__*/React__default.createElement(TableRow, {
        "aria-rowindex": "1"
      }, isExpansionActive && /*#__PURE__*/React__default.createElement(TableExpandHeader, _extends({
        tabIndex: "-1",
        enableExpando: hasExpandAll
      }, getExpandHeaderProps(), {
        id: "expand"
      })), renderSelectAll(getSelectionProps), columns.map(function (col, i) {
        return renderTableHeaders(col, getHeaderProps);
      }))), /*#__PURE__*/React__default.createElement(TableBody, {
        className: classNames(_defineProperty({}, "".concat(BASE_CSS, "--has-expansion"), isExpansionActive))
      }, rows.map(function (row, index) {
        // When it has batch actions and expansion one more column is required
        var colSpan = hasSelectAll || hasSelectRow ? columns.length + 2 : columns.length + 1;
        var rowData = dataSource.find(function (d) {
          return d.id === row.id;
        });
        return /*#__PURE__*/React__default.createElement(React__default.Fragment, {
          key: row.id
        }, isExpansionActive && renderExpandedRow(rowData) && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(TableExpandRow, _extends({
          key: row.id
        }, getRowProps({
          row: row
        }), {
          "aria-rowindex": index + 2,
          "data-aria-owns": "expandable-row-content-".concat(row.id)
        }), renderRow(row, columns, getSelectionProps, index)), /*#__PURE__*/React__default.createElement(TableExpandedRow, {
          colSpan: colSpan,
          className: expansionClassName,
          id: "expandable-row-content-".concat(row.id),
          role: "presentation"
        }, renderExpandedRow(rowData))), isExpansionActive && !renderExpandedRow(rowData) && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(TableRow, _extends({
          key: row.id
        }, getRowProps({
          row: row
        }), {
          "aria-rowindex": index + 2
        }), /*#__PURE__*/React__default.createElement("td", null, "\xA0"), renderRow(row, columns, getSelectionProps, index)), /*#__PURE__*/React__default.createElement("tr", {
          className: "".concat(BASE_CSS, "--expansion-placeholder"),
          "aria-rowindex": index + 2
        }, /*#__PURE__*/React__default.createElement("td", null, "\xA0"))), !isExpansionActive && /*#__PURE__*/React__default.createElement(TableRow, _extends({
          key: row.id
        }, getRowProps({
          row: row
        }), {
          "aria-rowindex": index + 2
        }), renderRow(row, columns, getSelectionProps, index)));
      }))));
    });
  };

  /**
   * Main Renderer
   */
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNames(FW_CSS, BASE_CSS, tableClassName),
    ref: tableRef
  }, renderDataTable(), renderPagination());
};

/**
 * PropTypes
 */
IlDataTableV2.propTypes = {
  title: PropTypes.string,
  description: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  isLoading: PropTypes.bool,
  pagination: PropTypes.shape(_objectSpread2(_objectSpread2({}, IlPaginationV2.propTypes), {}, {
    totalItems: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired
  })),
  stickyHeader: PropTypes.bool,
  toolbar: PropTypes.shape({
    search: PropTypes.shape(_objectSpread2({}, TranslatedIlSearchV2.propTypes)),
    filters: PropTypes.shape({
      renderFilters: PropTypes.func.isRequired,
      className: PropTypes.string
    }),
    buttons: PropTypes.arrayOf(PropTypes.shape(_objectSpread2(_objectSpread2({}, IlButton.propTypes), {}, {
      title: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      kind: PropTypes.string
    }))),
    visibleButtons: PropTypes.number,
    render: PropTypes.func
  }),
  batchActions: PropTypes.shape({
    hasSelectRow: PropTypes.bool.isRequired,
    selectRow: PropTypes.shape(_objectSpread2({}, TableSelectRow.propTypes)),
    hasSelectAll: PropTypes.bool.isRequired,
    selectAll: PropTypes.shape(_objectSpread2({}, TableSelectAll.propTypes)),
    actions: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    })),
    visibleActions: PropTypes.number
  }),
  expansion: PropTypes.shape({
    isActive: PropTypes.bool.isRequired,
    hasExpandAll: PropTypes.bool,
    renderExpandedRow: PropTypes.func.isRequired,
    className: PropTypes.string
  }),
  headers: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    isSortable: PropTypes.bool,
    alignment: PropTypes.oneOf(['center', 'right']),
    className: PropTypes.string,
    onSort: PropTypes.func,
    renderCell: PropTypes.func
  })).isRequired,
  dataSource: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired
  })).isRequired,
  canAutoFocus: PropTypes.bool,
  sortingDirections: PropTypes.array,
  defaultSortHeader: PropTypes.string,
  defaultSortDirection: PropTypes.number,
  onRenderProps: PropTypes.func
};

export { IlDataTableV2 };
